import { useCallback, useEffect, useState } from 'react';
import { Switch } from '@headlessui/react';
import { FiRefreshCw } from 'react-icons/fi';
import Link from 'components/Link';
import { PRICING_PAGE } from 'constants/pages';
import classNames from 'styles/utils/classNames';

const FREE_COOLDOWN_MS = 5 * 60 * 1000;

interface Props {
  hasPaidAccess: boolean;
  isUserSession: boolean;
  isBusy: boolean;
  isLiveUpdatesEnabled: boolean;
  onToggleLiveUpdates: (enabled: boolean) => void;
  onRefresh: () => void;
  onLoginRequired: () => void;
  variant?: 'desktop' | 'mobile';
}

export default function PositionsRefreshControl({
  hasPaidAccess,
  isUserSession,
  isBusy,
  isLiveUpdatesEnabled,
  onToggleLiveUpdates,
  onRefresh,
  onLoginRequired,
  variant = 'desktop',
}: Props) {
  const isMobile = variant === 'mobile';
  // Free logged-in users are rate-limited to one manual refresh every 5 minutes.
  // Paid users and anonymous (they get pushed to auth) are not gated here.
  const [freeCooldownEndAt, setFreeCooldownEndAt] = useState(0);
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    if (freeCooldownEndAt <= Date.now()) return;
    const id = setInterval(() => {
      const t = Date.now();
      setNowMs(t);
      if (t >= freeCooldownEndAt) clearInterval(id);
    }, 1000);
    return () => clearInterval(id);
  }, [freeCooldownEndAt]);

  const freeRemainingMs =
    isUserSession && !hasPaidAccess ? Math.max(0, freeCooldownEndAt - nowMs) : 0;
  const isFreeThrottled = freeRemainingMs > 0;

  const handleRefreshClick = useCallback(() => {
    if (!isUserSession) {
      onLoginRequired();
      return;
    }
    if (!hasPaidAccess && isFreeThrottled) return;
    onRefresh();
    if (!hasPaidAccess) {
      setFreeCooldownEndAt(Date.now() + FREE_COOLDOWN_MS);
    }
  }, [isUserSession, hasPaidAccess, isFreeThrottled, onRefresh, onLoginRequired]);

  const refreshDisabled = isBusy || (isUserSession && !hasPaidAccess && isFreeThrottled);

  const refreshButton = (
    <button
      type="button"
      title="Refresh prices"
      aria-label="Refresh prices"
      className={classNames(
        'flex items-center justify-center rounded-full bg-button-color text-white hover:bg-button-hover disabled:bg-gray-600',
        isMobile ? 'h-9 w-9' : 'ml-2 p-1.5',
      )}
      onClick={handleRefreshClick}
      disabled={refreshDisabled}
    >
      <FiRefreshCw
        className={classNames(isMobile ? 'h-4 w-4' : 'h-3 w-3', isBusy && 'animate-spin')}
      />
    </button>
  );

  if (hasPaidAccess) {
    return (
      <div className={classNames('flex items-center', isMobile ? 'gap-2' : 'gap-1.5')}>
        {refreshButton}
        <Switch.Group as="div" className="flex items-center">
          <Switch
            checked={isLiveUpdatesEnabled}
            onChange={onToggleLiveUpdates}
            className="group relative inline-flex h-3 w-6 flex-shrink-0 cursor-pointer items-center justify-center rounded-full focus:outline-none"
          >
            <span className="sr-only">Toggle live updates</span>
            <span
              aria-hidden="true"
              className={classNames(
                'pointer-events-none absolute mx-auto h-2 w-5 rounded-full transition-colors duration-200 ease-in-out',
                isLiveUpdatesEnabled ? 'bg-brand-primary' : 'bg-gray-600',
              )}
            />
            <span
              aria-hidden="true"
              className={classNames(
                'pointer-events-none absolute left-0 inline-block h-3 w-3 transform rounded-full border shadow ring-0 transition-transform duration-200 ease-in-out',
                isLiveUpdatesEnabled
                  ? 'translate-x-3 border-brand-primary bg-white'
                  : 'translate-x-0 border-gray-400 bg-gray-300',
              )}
            />
          </Switch>
          <span
            className={classNames(
              'ml-2 text-xs',
              isLiveUpdatesEnabled ? 'text-gray-200' : 'text-gray-400',
            )}
          >
            Live updates
          </span>
        </Switch.Group>
      </div>
    );
  }

  const seconds = Math.ceil(freeRemainingMs / 1000);
  const m = Math.floor(seconds / 60);
  const s = (seconds % 60).toString().padStart(2, '0');

  return (
    <div className={classNames('flex items-center', isMobile ? 'gap-2' : 'gap-1.5')}>
      {refreshButton}
      {isUserSession && isFreeThrottled ? (
        <span
          className="whitespace-nowrap rounded border border-palette-border bg-palette-black-3 px-1.5 py-0.5 text-[10px] text-gray-300"
          aria-live="polite"
        >
          Refresh in {m}:{s}
        </span>
      ) : null}
      <Link
        href={PRICING_PAGE}
        className="whitespace-nowrap rounded border border-brand-primary/35 bg-brand-primary/10 px-1.5 py-0.5 text-[10px] font-semibold text-brand-primary hover:bg-brand-primary/20"
      >
        Upgrade for live updates
      </Link>
    </div>
  );
}
